import { getLogger as getBaseLogger } from "@marble/logger";
import {
  InsuranceInfo,
  Student,
  type State,
  type Therapist,
} from "@prisma/client";
import type { Logger } from "pino";

export type ExtendedLogger = Logger & {
  addAttributes: (attributes: Record<string, any>) => ExtendedLogger;
  withAppointmentId: (appointmentId: number) => ExtendedLogger;
  withUserId: (userId: number) => ExtendedLogger;
  withInsuranceInfoId: (insuranceInfoId: number) => ExtendedLogger;
  withInsuranceInfo: (insuranceInfo: InsuranceInfo) => ExtendedLogger;
  withStudent: (student: Student) => ExtendedLogger;
  withTherapist: (therapist: Therapist) => ExtendedLogger;
  withClientUser: (clientUser: ClientUserForLogger) => ExtendedLogger;
  withStudentId: (studentId: number) => ExtendedLogger;
  withAdminId: (adminId: number) => ExtendedLogger;
};

/** The client's User; only its Student profile is logged. */
type ClientUserForLogger = {
  id: number;
  Student?: { id: number; state: State | null } | null;
};

const getLogger = (
  name: string,
  attributes: Record<string, any> = {},
): ExtendedLogger => {
  let baseLogger = getBaseLogger(name, attributes) as ExtendedLogger;

  baseLogger.addAttributes = (attributes: Record<string, any>) => {
    baseLogger = baseLogger.child({
      ...baseLogger.bindings,
      ...attributes,
    }) as ExtendedLogger;
    return baseLogger;
  };

  baseLogger.withAppointmentId = (appointmentId: number) => {
    baseLogger = baseLogger.child({
      ...baseLogger.bindings,
      appointmentId,
    }) as ExtendedLogger;
    return baseLogger;
  };

  baseLogger.withUserId = (userId: number) => {
    baseLogger = baseLogger.child({
      ...baseLogger.bindings,
      userId,
    }) as ExtendedLogger;
    return baseLogger;
  };

  baseLogger.withStudentId = (studentId: number) => {
    baseLogger = baseLogger.child({
      ...baseLogger.bindings,
      studentId,
    }) as ExtendedLogger;
    return baseLogger;
  };

  baseLogger.withAdminId = (adminId: number) => {
    baseLogger = baseLogger.child({
      ...baseLogger.bindings,
      adminId,
    }) as ExtendedLogger;
    return baseLogger;
  };

  baseLogger.withInsuranceInfoId = (insuranceInfoId: number) => {
    baseLogger = baseLogger.child({
      ...baseLogger.bindings,
      insuranceInfoId,
    }) as ExtendedLogger;
    return baseLogger;
  };

  baseLogger.withInsuranceInfo = (insuranceInfo: InsuranceInfo) => {
    baseLogger = baseLogger.child({
      ...baseLogger.bindings,
      insuranceInfoId: insuranceInfo.id,
      studentId: insuranceInfo.studentId,
      guardianId: insuranceInfo.guardianId,
      insurancePayerId: insuranceInfo.insurancePayerId,
      lineOfBusiness: insuranceInfo.lineOfBusiness,
      claimsProcessor: insuranceInfo.claimsProcessor,
      state: insuranceInfo.state,
    }) as ExtendedLogger;
    return baseLogger;
  };

  baseLogger.withStudent = (student: Student) => {
    baseLogger = baseLogger.child({
      ...baseLogger.bindings,
      studentId: student.id,
      userId: student.userId,
      state: student.state,
    }) as ExtendedLogger;
    return baseLogger;
  };

  baseLogger.withTherapist = (therapist: Therapist) => {
    baseLogger = baseLogger.child({
      ...baseLogger.bindings,
      therapistId: therapist.id,
      therapistUserId: therapist.userId,
    }) as ExtendedLogger;
    return baseLogger;
  };

  baseLogger.withClientUser = (clientUser: ClientUserForLogger) => {
    baseLogger = baseLogger.child({
      ...baseLogger.bindings,
      clientUserId: clientUser.id,
      studentId: clientUser.Student?.id,
      state: clientUser.Student?.state,
    }) as ExtendedLogger;
    return baseLogger;
  };

  return baseLogger;
};

export default getLogger;
